import { AlertCircle } from "lucide-react";
import type { FC } from "react";

import {
  CheckboxField,
  ControlField,
  DateField,
  FormSheet,
  TextField
} from "@/components/form";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  useCustomCaseTypes,
  useLawyerFirmId
} from "@/features/customCaseTypes/useCustomCaseTypes";

import type { MatterDTO } from "../../../../api/types/matter";
import {
  RETIRED_VISAS,
  RFE_VISA_CATEGORY_ID
} from "../../../../components/common/visaOptions";
import { useGetUserDetails } from "../../../../hooks/useGetUserDetails";
import { TeamMembersPicker } from "../../components/TeamMembersPicker";
import { VisaPicker } from "../../components/VisaPicker";
import {
  noticeWorkflowLabel,
  useCreateCaseForm,
  type CreateCaseValues
} from "./useCreateCaseForm";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  /** Prefill when reopening from onboarding ("change case type"). */
  initialMatter?: MatterDTO | null;
  onCreated: (matter: MatterDTO) => void;
};

/**
 * New / update case: visa workflow (with the underlying visa for RFE and
 * NOID/NOIR notices), filing deadline and assignees. Update mode creates the
 * new case; the caller swaps out the old one.
 */
export const CreateCaseSheet: FC<Props> = ({
  open,
  onOpenChange,
  clientId,
  initialMatter = null,
  onCreated
}) => {
  const {
    userDetails: { id: currentUserId }
  } = useGetUserDetails();
  const lawFirmId = useLawyerFirmId();
  const customTypes = useCustomCaseTypes(lawFirmId);
  const {
    form,
    isEditing,
    notice,
    nameable,
    workflow,
    isSaving,
    error,
    setWorkflow,
    submit
  } = useCreateCaseForm({
    open,
    clientId,
    currentUserId: currentUserId ?? "",
    initialMatter,
    onCreated,
    lawFirmId,
    onTypeSaved: customTypes.refresh
  });

  return (
    <FormSheet
      form={form}
      open={open}
      onOpenChange={onOpenChange}
      title={isEditing ? "Update case" : "New case"}
      description={
        isEditing
          ? "Change the visa workflow or filing details for this client."
          : "Start a new case for this client."
      }
      onSubmit={submit}
      isEditing={isEditing}
      isSubmitting={isSaving}
      submitLabel={isEditing ? "Update case" : "Save case"}
    >
      <ControlField<CreateCaseValues, "workflow">
        name="workflow"
        label="Visa Workflow"
        required
        disabled={isSaving}
      >
        {(field, { id, invalid }) => (
          <VisaPicker
            id={id}
            value={field.value}
            onChange={setWorkflow}
            invalid={invalid}
            disabled={isSaving}
            excludeVisas={RETIRED_VISAS}
            customTypes={customTypes.types}
          />
        )}
      </ControlField>

      {nameable ? (
        <>
          <TextField<CreateCaseValues>
            name="customName"
            label="Custom Workflow Name"
            placeholder="EB-1A + Consular"
            description='Shows as "Custom: <name>" on the case. Leave blank for an unnamed Custom Workflow.'
            disabled={isSaving}
          />
          {customTypes.canManage ? (
            <CheckboxField<CreateCaseValues>
              name="saveForFirm"
              label="Save for the Firm"
              description="Everyone at the firm can pick it next time. Set its letters and templates in Settings > Case Preparation."
              disabled={isSaving}
            />
          ) : null}
        </>
      ) : null}

      {notice ? (
        <ControlField<CreateCaseValues, "noticeVisa">
          name="noticeVisa"
          label={`Select Visa Type for ${noticeWorkflowLabel(workflow)}`}
          required
          disabled={isSaving}
        >
          {(field, { id, invalid }) => (
            <VisaPicker
              id={id}
              value={field.value}
              onChange={field.onChange}
              invalid={invalid}
              disabled={isSaving}
              excludeCategories={[RFE_VISA_CATEGORY_ID]}
              excludeVisas={RETIRED_VISAS}
            />
          )}
        </ControlField>
      ) : null}

      <DateField<CreateCaseValues>
        name="filingDeadline"
        label="Filing Deadline"
        disabled={isSaving}
      />

      <ControlField<CreateCaseValues, "assignedUserIds">
        name="assignedUserIds"
        label="Assigned Team Members"
        disabled={isSaving}
      >
        {(field, { id }) => (
          <TeamMembersPicker
            id={id}
            value={field.value}
            onChange={field.onChange}
            disabled={isSaving}
          />
        )}
      </ControlField>

      {error ? (
        <Alert variant="destructive">
          <AlertCircle />
          <AlertTitle>Couldn&apos;t create case</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}
    </FormSheet>
  );
};
